import { useState } from "react"
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import useTranslate from "hooks/use-translate-language";
import { Card, Spinner } from "components/ui";
import browserHistory from "browser-history";
import selectors from "selectors";
import "./notification.scss";
import { IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { formatDateTimeLocal } from "utils/misc";
import UnconfirmedAppointmentActions from "../upcoming-chats/unconfirmed-appointment-actions";
import { updateNotificationToSeen } from "actions/notifications";
import { NOTIFICATION_TYPES, NOTIFICATION_ROUTES } from "constants/notification-types";

const NotificationItem = (props) => {
  // Give spanish version if you speak spanish but give English if there is no Spanish version
  const [isFetching, setIsFetching] = useState(false)

  const translate = useTranslate();
  const speaksSpanish = useSelector(selectors.getSpeaksSpanish);
  const dispatch = useDispatch();

  const cardClassName = props.isNew
    ? "notification-card notification-card-new"
    : "notification-card";

  const handleCloseNotification = async (e) => {
    e.stopPropagation();

    setIsFetching(true);
    await dispatch(updateNotificationToSeen(props.notification_id));
    if (props.isNew && props.decreaseAmountOfNewNotifications) {
      props.decreaseAmountOfNewNotifications();
    }
    setIsFetching(false);
  };
  const handleNavigation = () => {
    const route = NOTIFICATION_ROUTES[props.notification_type];
    if (route) {
      browserHistory.push(route);
    }
  };
  const notificationTitle = !speaksSpanish ? props.title : props.spanish_title ?? props.title;
  const ariaLabel = `${props.isNew ? `${translate("newNotification")}: ` : (props.index != null ? `${translate("notification")} ${props.index+1}: ` : `${translate("notification")}: `)}${notificationTitle}. ${translate("activateToViewDetails")}`;

  return (
    <Card
      className={cardClassName}
      onClick={handleNavigation}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleNavigation(); } }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-describedby={`notification-description-${props.notification_id}`}
    >
      <div className="notification-header">
        <h4
          id={`notification-title-${props.notification_id}`}
          className={props.isNew ? "notification-title notification-title-new" : "notification-title"}>
          {notificationTitle}
        </h4>
        {isFetching
          ? <Spinner aria-label="Loading notification" />
          : <IconButton
            onClick={(e) => handleCloseNotification(e)}
            size="small"
            style={{ color: "#337abb" }}
            aria-label="Close notification"
          >
            <CloseIcon />
          </IconButton>}
      </div>
      <div className="notification-content">
        <div>
          <div
            id={`notification-description-${props.notification_id}`}
            className="notification-description">
            {!speaksSpanish
              ? props.description
              : props.spanish_description ?? props.description}
          </div>

          <div style={{ float: "left" }}>
            <NotificationTimeStamp {...props} />
          </div>

          <div style={{ float: "right" }}>
            <NotificationActions {...props} />
          </div>
        </div>
      </div>
    </Card>

  );
};

NotificationItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  spanish_title: PropTypes.string.isRequired,
  spanish_description: PropTypes.string,
  notification_date: PropTypes.string,
  notification_type: PropTypes.number,
  notification_id: PropTypes.number,
  end_date: PropTypes.string,
  start_date: PropTypes.string,
  isNew: PropTypes.bool,
  decreaseAmountOfNewNotifications: PropTypes.func,
  index: PropTypes.number
};

export default NotificationItem;

const NotificationTimeStamp = ({ notification_date, start_date }) => {
  return (
    <div className="notification-footer">
      <span className="notification-date-text">
        {start_date
          ? formatDateTimeLocal(start_date)
          : formatDateTimeLocal(notification_date)
        }
      </span>
    </div>
  );
};

NotificationTimeStamp.propTypes = {
  notification_date: PropTypes.string.isRequired,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
};

const NotificationActions = (props) => {
  const { notification_type, end_date, start_date } = props;

  // Helper to check if appointment is still upcoming
  const isAppointmentUpcoming = () => {
    const appointmentDate = end_date || start_date;
    if (!appointmentDate) return false;

    // Compare Date objects directly instead of formatted strings
    const now = new Date();
    const eventDate = new Date(appointmentDate);
    return now < eventDate;
  };

  switch (notification_type) {
    case NOTIFICATION_TYPES.UPCOMING_TEXT_CHAT:
    case NOTIFICATION_TYPES.UPCOMING_VIDEO_CHAT:
    case NOTIFICATION_TYPES.UPCOMING_DEMO_CHAT:
    case NOTIFICATION_TYPES.CONFIRM_CHAT_REQUEST:
      return isAppointmentUpcoming() ? <UnconfirmedAppointmentActions {...props} /> : null;

    case NOTIFICATION_TYPES.CHAT_CANCELLED:
      return <div>Cancel Chat</div>;

    case NOTIFICATION_TYPES.PRODUCT_QUESTIONNAIRE:
    case NOTIFICATION_TYPES.SERVICE_QUESTIONNAIRE:
      return null;

    case NOTIFICATION_TYPES.AUTOFIT_ELIGIBLE:
      // case NOTIFICATION_TYPES.AUTOFIT_RESULTS: // Commented in original
      return <MaskFittingNotificationActions {...props} />;

    case NOTIFICATION_TYPES.NEW_FILE_UPLOAD:
      return <FileNotificationActions {...props} />;

    case NOTIFICATION_TYPES.USER_UPDATED_PROFILE:
    case NOTIFICATION_TYPES.ADMIN_UPDATED_PROFILE:
      return null;

    case NOTIFICATION_TYPES.SIGNATURE_DOCUMENT_ASSIGNED:
      return <SignatureNotificationActions {...props} />;

    default:
      return null;
  }
};
NotificationActions.propTypes = {
  notification_type: PropTypes.number.isRequired,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
};

const MaskFittingNotificationActions = () => (
  <div className="notification-actions">
    <Button
      onClick={() => browserHistory.push("/mask_fitting")}
      color="primary"
    >
      Start Mask Fitting
    </Button>
  </div>
);
MaskFittingNotificationActions.propTypes = {
  title: PropTypes.string,
  accepted: PropTypes.bool,
  description: PropTypes.string,
};

const FileNotificationActions = ({ accepted }) => (
  <div className="notification-actions">
    {!accepted && (
      <Button onClick={() => browserHistory.push("/documents")} color="primary">
        View
      </Button>
    )}
  </div>
);

FileNotificationActions.propTypes = {
  accepted: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
};

const SignatureNotificationActions = ({ accepted }) => (
  <div className="notification-actions">
    {!accepted && (
      <Button
        onClick={() => browserHistory.push("/signatures")}
        color="primary"
      >
        Sign Now
      </Button>
    )}
  </div>
);
SignatureNotificationActions.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  accepted: PropTypes.bool,
};